/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManagerFn,
  RequiredItem,
} from '../../../../registry/Component.type';
import { baseComponentManager } from '../../../../registry/Components';
import { toOptions } from '../../../../utils/ManagerUtils';
import { PivotColumnDefaultProps } from '../PivotTableStringColumn/PivotTableStringColumnManager';
import { defaultValue } from './PivotTableCalculatedColumn';

const PivotTableCalculatedColumnManager: ComponentManagerFn<
  'PivotTableCalculatedColumn'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'PivotTableCalculatedColumn'>(
      platform,
      'PivotTableCalculatedColumn',
    ),
    type: 'PivotTableCalculatedColumn',
    name: 'Pivot Table Calculated Column',
    isChildAllowed: () => false,
    defaultValue,
    props: {
      ...PivotColumnDefaultProps,
      expression: {
        label: 'Expression',
        type: 'expressionOnly',
        returnType: (item: RequiredItem<'PivotTableCalculatedColumn'>) =>
          item.props.type,
      },
      type: {
        label: 'Value Type',
        type: 'select',
        options: toOptions(['string', 'number']),
      },
      showNegativeInBrackets: {
        label: 'Show Negative In Brackets',
        type: 'boolean',
        helpText: 'Enable this property to show negative values in brackets.',
      },
    },
    isPropertyVisible(property, itemProps, context) {
      if (property === 'showNegativeInBrackets') {
        return itemProps.type === 'number';
      }
      return true;
    },
    getDisplayText: (item) => item.props.viewAttribute,
    category: 'Pivot Table',
    icon: { iconName: 'sigma' },
    description: `**Overview**<br/>
The Pivot Table Calculated Column is a specialized column component used within a Pivot Table to display values derived from custom logic rather than direct database fields. It allows you to inject mathematical formulas, text concatenation, or logical operations directly into your data grid.
Use this component when you need to show metrics like "Profit Margin," "Variance," or "Full Name" that do not exist as distinct columns in your underlying data source.

**1. Calculation Logic**<br/>
The core function of this component is defined by how it processes and transforms data row-by-row or within the pivot context.
* **Expression:** This is the primary property where you define your logic. You write a formula that computes the value for each cell in the column using data from other attributes in the store.
* **Value Type:** You must specify the data type of the resulting value to ensure correct sorting and alignment:
    * **Number:** For mathematical calculations (e.g., \`Revenue - Cost\`).
    * **String:** For text manipulation or descriptive flags (e.g., concatenating "First Name" and "Last Name").

**2. Formatting Options**<br/>
You can control how the calculated results appear to the user to ensure professional reporting standards.
* **Show Negative In Brackets:** Specific to numeric types, this boolean property changes the display format of negative numbers. Instead of showing **-500**, it will render as **(500)**. This is a standard convention in financial and accounting reporting.
* **Standard Props:** It inherits standard column properties, allowing you to set custom **Headers**, **Width**, and **Horizontal Alignment** similar to standard data columns.

**3. Best Use Cases**<br/>
* **Financial Ratios:** Calculating "Gross Profit %" by dividing Profit by Revenue directly within the pivot view.
* **Data Concatenation:** Combining "City" and "Country" fields into a single "Location" column for cleaner reporting.
* **Variance Analysis:** Computing the difference between "Budgeted Amount" and "Actual Spend" columns dynamically.
* **Conditional Flags:** Creating a text-based column that outputs "Critical," "Warning," or "Normal" based on the threshold values of another numeric column.
* **Date Calculations:** Calculating the number of days between two date fields to show "Aging" or "Lead Time."`,
  };
};

export default PivotTableCalculatedColumnManager;
